import { useEffect, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { START_TOUCH } from '@confluence/navdex';

import type { FeedContentQuery } from './__types__/FeedContentQuery';
import { FeedType } from './feedTypeEnum';
import type { FeedPopularFeedQuery } from './__types__/FeedPopularFeedQuery';

export const useFeedRenderedTrackEvent = (
	feedType: FeedType | string,
	followingFeedData: FeedContentQuery | undefined,
	popularFeedData: FeedPopularFeedQuery | undefined,
): void => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const isFollowing = feedType === FeedType.FOLLOWING;
	const isPopular = feedType === FeedType.POPULAR;
	const hasData = isFollowing ? Boolean(followingFeedData) : Boolean(popularFeedData);
	const resultCount =
		(isFollowing
			? followingFeedData?.feed?.nodes?.length
			: popularFeedData?.popularFeed.nodes.length) || 0;
	const spacesFollowing = isFollowing
		? followingFeedData?.getFeedUserConfig?.spaces?.length || 0
		: undefined;
	const peopleFollowing = isFollowing
		? followingFeedData?.getFeedUserConfig?.users?.length || 0
		: undefined;

	// should fire the "rendered" analytics event only once,
	// including when switching between feed types
	const renderedEventLastFeedTypeRef = useRef('');
	const firedRenderedEvent = feedType === renderedEventLastFeedTypeRef.current;

	useEffect(() => {
		if (hasData && !firedRenderedEvent) {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					source: 'homeScreen',
					action: 'rendered',
					actionSubject: 'feed',
					actionSubjectId: 'feedAlpha',
					attributes: {
						feedType: feedType.toLowerCase(),
						navdexPointType: START_TOUCH,
						homeVersion: 'visualRefresh',
						...(isFollowing || isPopular
							? {
									resultCount,
									spacesFollowing,
									peopleFollowing,
								}
							: {}),
					},
				},
			}).fire();
			renderedEventLastFeedTypeRef.current = feedType;
		}
	}, [
		hasData,
		feedType,
		resultCount,
		spacesFollowing,
		peopleFollowing,
		firedRenderedEvent,
		isFollowing,
		isPopular,
		createAnalyticsEvent,
	]);
};
